export const SECTION_KEYS = [
  "hero",
  "workflow",
  "caseStudy",
  "sectors",
  "testimonials",
  "faq",
  "customBlocks",
  "finalCta",
];

export const DEFAULT_LANDING_PAGE_CONTENT = {
  theme: {
    accentColor: "#f97316",
  },
  layout: {
    sectionOrder: [...SECTION_KEYS],
    sectionColors: {
      hero: "#f97316",
      workflow: "#0ea5e9",
      caseStudy: "#22c55e",
      sectors: "#a78bfa",
      testimonials: "#f59e0b",
      faq: "#14b8a6",
      customBlocks: "#ef4444",
      finalCta: "#3b82f6",
    },
  },
  navigation: {
    items: [
      { label: "Workflow", href: "#workflow" },
      { label: "Resultaten", href: "#resultaten" },
      { label: "Sectoren", href: "#sectoren" },
    ],
    loginLabel: "Inloggen",
    loginHref: "https://app.logboek24.nl",
    demoLabel: "Demo aanvragen",
    demoHref: "mailto:info@logboek24.nl",
  },
  footer: {
    copyright: "uitvoering, planning en historie in een platform.",
    links: [
      { label: "Website", href: "https://www.logboek24.nl" },
      { label: "App", href: "https://app.logboek24.nl" },
      { label: "Contact", href: "mailto:info@logboek24.nl" },
    ],
  },
  seo: {
    title: "Logboek24 | Grip op uitvoering en historie",
    description:
      "Logboek24 brengt werkbonnen, planning, projectcontrol, inspecties en rapportage samen in een operationeel dashboard voor installatie- en serviceteams.",
    ogTitle: "Logboek24 | Grip op uitvoering en historie",
    ogDescription:
      "Van storing en werkbon tot inspectie, projectcontrol en rapportage: alles in een centrale operatieflow.",
  },
  hero: {
    eyebrow: "Dashboard first",
    title: "Geen hero-truc, wel direct overzicht op uitvoering.",
    description:
      "Laat direct zien wat er speelt: open werkbonnen, storingen, inspecties en documentstatus. Zo zien bezoekers binnen seconden dat dit een operationeel platform is.",
    primaryCtaLabel: "Plan een demo",
    primaryCtaHref: "mailto:info@logboek24.nl",
    secondaryCtaLabel: "Bekijk resultaten",
    secondaryCtaHref: "#resultaten",
  },
  signals: [
    { title: "Aantoonbaar", text: "Elke actie met context en historie." },
    { title: "Snel stuurbaar", text: "Afwijkingen direct zichtbaar." },
    { title: "Audit klaar", text: "Inspectie en rapportage op een lijn." },
  ],
  dashboard: {
    overviewLabel: "Vandaag - overzicht",
    clusterName: "Installatiecluster Zuid",
    statusLabel: "Op schema",
    kpis: [
      { label: "Werkbonnen", value: "12", meta: "Openstaand vandaag" },
      { label: "Storingen", value: "3", meta: "Met prioriteit" },
      { label: "Inspecties", value: "8", meta: "Komende 30 dagen" },
      { label: "Documenten", value: "247", meta: "Rapportage deze maand" },
    ],
    flowLabel: "Live workflow",
    flowPeriodLabel: "Laatste 24 uur",
    flowRows: [
      { label: "Werkbon #WB-2193 naar uitvoering", value: "2 min geleden" },
      { label: "Inspectierapport opgeleverd", value: "18 min geleden" },
      { label: "Inkoopactie materiaal gestart", value: "34 min geleden" },
    ],
  },
  workflow: {
    eyebrow: "Workflow",
    title: "Probleem naar oplossing in een vaste operatieflow",
    description:
      "Geen losse schermen, maar een doorlopend proces van werkbon tot uitvoering, projectcontrol en rapportage met duidelijke verantwoordelijkheid per rol.",
    steps: [
      {
        badge: "Stap 1",
        title: "Werkbon en voorbereiding",
        body: "Werkbon met locatie, scope, materialen en checklist in een vaste statusflow.",
      },
      {
        badge: "Stap 2",
        title: "Planning en uitvoering",
        body: "Monteurtoewijzing, starttijd en voortgang live zichtbaar voor kantoor en buitendienst.",
      },
      {
        badge: "Stap 3",
        title: "Inspectie en overdracht",
        body: "Inspectiepunten, fotos, opleverstatus en rapportage direct terug in de projecthistorie.",
      },
    ],
  },
  caseStudy: {
    eyebrow: "Case first",
    title: "Eerst bewijs, daarna verhaal",
    description:
      "Hieronder staat een anoniem praktijkvoorbeeld van implementatie naar operationele winst.",
    ctaLabel: "Vraag praktijkcase aan",
    ctaHref: "mailto:info@logboek24.nl",
    timeline: [
      {
        year: "Week 1",
        title: "Nulmeting bij serviceorganisatie",
        body: "Werkboninformatie zat verspreid over mail, telefoon en losse notities.",
        tag: "Start",
        detail:
          "Projectleiding en buitendienst werkten met verschillende overzichten, waardoor statusvragen veel nabellen vroegen.",
      },
      {
        year: "Week 2",
        title: "Proces ingericht",
        body: "Werkbon, planning, inspectie en rapportage in een centrale workflow gezet.",
        tag: "Inrichting",
        detail:
          "Per rol zijn vaste statusovergangen en verantwoordelijkheden ingesteld, zodat overdracht niet afhankelijk is van losse communicatie.",
      },
      {
        year: "Week 4",
        title: "Eerste operationele winst",
        body: "Minder nabellen over status en snellere overdracht naar projectleiding.",
        tag: "Effect",
        detail:
          "Statusupdates kwamen direct uit de uitvoering terug in hetzelfde overzicht dat planning en projectleiding gebruikten.",
      },
      {
        year: "Week 8",
        title: "Opschalen naar meerdere teams",
        body: "Zelfde werkwijze toegepast op meerdere monteurteams en regio's.",
        tag: "Opschaling",
        detail:
          "Door dezelfde structuur te herhalen bleef rapportage vergelijkbaar tussen teams en konden afwijkingen sneller worden opgepakt.",
      },
    ],
  },
  sectors: {
    eyebrow: "Gebouwd voor jouw sector",
    title: "Een platform voor teams die op aantoonbaarheid draaien",
    description:
      "Of je nu onderhoud, projectwerk of inspectieprocessen draait, de kern blijft hetzelfde: zichtbaar werk, duidelijke status en betrouwbare historie.",
    cards: [
      {
        title: "Vastgoed en beheer",
        body: "Aantoonbare onderhoudshistorie met vaste inspectie- en rapportagecyclus.",
      },
      {
        title: "Installatiebedrijven",
        body: "Van storing en werkbon naar uitvoering, nacontrole en overdracht zonder gaten.",
      },
      {
        title: "Industrie",
        body: "Controle op kritische assets met planning, opvolging en audittrail in een lijn.",
      },
      {
        title: "Zorg en onderwijs",
        body: "Veiligheid, compliance en terugvindbaarheid in een duidelijke structuur.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Wat klanten merken",
    title: "Ervaring uit de praktijk",
    items: [
      {
        name: "Serviceco Zuid",
        role: "Projectleiding",
        quote:
          "We hebben minder statusvragen en meer rust in de overdracht tussen kantoor en buitendienst.",
      },
      {
        name: "Installatieteam West",
        role: "Werkvoorbereiding",
        quote:
          "Planning en uitvoering zitten nu in dezelfde flow, daardoor is de voortgang veel beter voorspelbaar.",
      },
      {
        name: "Technisch Beheer Noord",
        role: "Operations",
        quote:
          "Voor audits kunnen we sneller aantonen wat wanneer is gebeurd en door wie.",
      },
    ],
  },
  faq: {
    eyebrow: "FAQ",
    title: "Veelgestelde vragen",
    items: [
      {
        question: "Kunnen we met meerdere teams en locaties werken?",
        answer:
          "Ja, je kunt dezelfde workflow gebruiken over meerdere teams en regio's met centrale rapportage.",
      },
      {
        question: "Ondersteunt het platform inspecties en rapportage?",
        answer:
          "Ja, inspectiepunten, bijlagen en rapportages zijn onderdeel van dezelfde operationele lijn.",
      },
      {
        question: "Is dit geschikt voor audit- en compliance-eisen?",
        answer:
          "Ja, acties worden vastgelegd met context en historie, wat helpt bij aantoonbaarheid.",
      },
    ],
  },
  customBlocks: [
    {
      title: "Implementatie in stappen",
      body: "Start met een kernteam en schaal daarna gecontroleerd op basis van een vaste template.",
      ctaLabel: "Plan intake",
      ctaHref: "mailto:info@logboek24.nl",
    },
    {
      title: "Integraties",
      body: "Koppel met bestaande systemen zodat dubbel invoerwerk beperkt blijft.",
      ctaLabel: "Bekijk koppelingen",
      ctaHref: "mailto:info@logboek24.nl",
    },
  ],
  finalCta: {
    eyebrow: "Volgende stap",
    title: "Klaar om dit door te vertalen naar jouw echte processen?",
    description:
      "We vullen samen jouw echte cases, workflow en KPI-taal in zodat de homepage direct klopt met de praktijk op de werkvloer.",
    primaryCtaLabel: "Plan demo",
    primaryCtaHref: "mailto:info@logboek24.nl",
    secondaryCtaLabel: "Naar de app",
    secondaryCtaHref: "https://app.logboek24.nl",
  },
};

function asString(value, fallback = "") {
  const text = typeof value === "string" ? value : String(value ?? "");
  const trimmed = text.trim();
  return trimmed || fallback;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asHexColor(value, fallback = "#f97316") {
  const raw = asString(value, fallback);
  const normalized = raw.startsWith("#") ? raw : `#${raw}`;
  return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized.toLowerCase() : fallback;
}

function normalizeItem(item, fallback, keys) {
  const source = item && typeof item === "object" ? item : {};
  const next = {};
  for (const key of keys) {
    next[key] = asString(source[key], fallback[key] || "");
  }
  return next;
}

function normalizeList(items, fallbackItems, keys, max = 12) {
  const incoming = asArray(items);
  const baseline = asArray(fallbackItems);
  const source = incoming.length ? incoming : baseline;
  return source.slice(0, max).map((item, index) => {
    const fb = baseline[index] || baseline[baseline.length - 1] || {};
    return normalizeItem(item, fb, keys);
  });
}

function normalizeSectionOrder(order, fallbackOrder) {
  const requested = asArray(order)
    .map((key) => asString(key, ""))
    .filter((key) => SECTION_KEYS.includes(key));

  const unique = [];
  for (const key of requested) {
    if (!unique.includes(key)) unique.push(key);
  }

  for (const key of fallbackOrder) {
    if (!unique.includes(key)) unique.push(key);
  }

  return unique;
}

function normalizeSectionColors(rawColors, fallbackColors) {
  const source = rawColors && typeof rawColors === "object" ? rawColors : {};
  const normalized = {};
  for (const key of SECTION_KEYS) {
    normalized[key] = asHexColor(source[key], fallbackColors[key]);
  }
  return normalized;
}

export function normalizeLandingPageContent(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const fb = DEFAULT_LANDING_PAGE_CONTENT;

  return {
    theme: {
      accentColor: asHexColor(source.theme?.accentColor, fb.theme.accentColor),
    },
    layout: {
      sectionOrder: normalizeSectionOrder(source.layout?.sectionOrder, fb.layout.sectionOrder),
      sectionColors: normalizeSectionColors(source.layout?.sectionColors, fb.layout.sectionColors),
    },
    navigation: {
      items: normalizeList(source.navigation?.items, fb.navigation.items, ["label", "href"], 8),
      loginLabel: asString(source.navigation?.loginLabel, fb.navigation.loginLabel),
      loginHref: asString(source.navigation?.loginHref, fb.navigation.loginHref),
      demoLabel: asString(source.navigation?.demoLabel, fb.navigation.demoLabel),
      demoHref: asString(source.navigation?.demoHref, fb.navigation.demoHref),
    },
    footer: {
      copyright: asString(source.footer?.copyright, fb.footer.copyright),
      links: normalizeList(source.footer?.links, fb.footer.links, ["label", "href"], 8),
    },
    seo: normalizeItem(source.seo, fb.seo, ["title", "description", "ogTitle", "ogDescription"]),
    hero: normalizeItem(source.hero, fb.hero, [
      "eyebrow",
      "title",
      "description",
      "primaryCtaLabel",
      "primaryCtaHref",
      "secondaryCtaLabel",
      "secondaryCtaHref",
    ]),
    signals: normalizeList(source.signals, fb.signals, ["title", "text"], 6),
    dashboard: {
      ...normalizeItem(source.dashboard, fb.dashboard, [
        "overviewLabel",
        "clusterName",
        "statusLabel",
        "flowLabel",
        "flowPeriodLabel",
      ]),
      kpis: normalizeList(source.dashboard?.kpis, fb.dashboard.kpis, ["label", "value", "meta"], 8),
      flowRows: normalizeList(source.dashboard?.flowRows, fb.dashboard.flowRows, ["label", "value"], 10),
    },
    workflow: {
      ...normalizeItem(source.workflow, fb.workflow, ["eyebrow", "title", "description"]),
      steps: normalizeList(source.workflow?.steps, fb.workflow.steps, ["badge", "title", "body"], 8),
    },
    caseStudy: {
      ...normalizeItem(source.caseStudy, fb.caseStudy, [
        "eyebrow",
        "title",
        "description",
        "ctaLabel",
        "ctaHref",
      ]),
      timeline: normalizeList(
        source.caseStudy?.timeline,
        fb.caseStudy.timeline,
        ["year", "title", "body", "tag", "detail"],
        12
      ),
    },
    sectors: {
      ...normalizeItem(source.sectors, fb.sectors, ["eyebrow", "title", "description"]),
      cards: normalizeList(source.sectors?.cards, fb.sectors.cards, ["title", "body"], 12),
    },
    testimonials: {
      ...normalizeItem(source.testimonials, fb.testimonials, ["eyebrow", "title"]),
      items: normalizeList(source.testimonials?.items, fb.testimonials.items, ["name", "role", "quote"], 8),
    },
    faq: {
      ...normalizeItem(source.faq, fb.faq, ["eyebrow", "title"]),
      items: normalizeList(source.faq?.items, fb.faq.items, ["question", "answer"], 12),
    },
    customBlocks: normalizeList(
      source.customBlocks,
      fb.customBlocks,
      ["title", "body", "ctaLabel", "ctaHref"],
      12
    ),
    finalCta: normalizeItem(source.finalCta, fb.finalCta, [
      "eyebrow",
      "title",
      "description",
      "primaryCtaLabel",
      "primaryCtaHref",
      "secondaryCtaLabel",
      "secondaryCtaHref",
    ]),
  };
}
